import * as React from 'react';
import { useCallback } from 'react';

import { Icon } from '@rbilabs/universal-components';
import { useIntl } from 'react-intl';

import { ActionButtonVariants } from 'components/action-button';
import { CustomEventNames, EventTypes, logEvent } from 'state/amplitude';
import { useAuthContext } from 'state/auth';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useOrderContext } from 'state/order';
import { AccessibleMainContent, useUIContext } from 'state/ui';
import { primitive } from 'styles/constants/primitives';
import { routes } from 'utils/routing';
import { isDelivery, isPickup } from 'utils/service-mode';

import { ActionButtonStyled } from '../styled';
import { ICTAButtonProps, ICTAButtonsProps } from '../types';

const CTAButton = ({
  children,
  iconVariant,
  primary,
  defaultLinkTo,
  isMatchingServiceMode,
  numberOfItemsInCart,
  navigate,
  marginTop,
  eventName,
  itemId,
  backgroundColor,
  _text,
  validatePreCurbsideOrder,
}: ICTAButtonProps) => {
  const shouldGoToCart = isMatchingServiceMode && numberOfItemsInCart > 0;
  const { formatMessage } = useIntl();
  const { setCancelCurbsideOrderCallbackUrl } = useOrderContext();
  const { isAuthenticated, openSignInModal } = useAuthContext();
  const enableSignupAfterCart = useFlag(LaunchDarklyFlag.ENABLE_MOVE_SIGNUP_AFTER_CART);

  const handleOnPress = useCallback(() => {
    let path: string = defaultLinkTo;
    path = isMatchingServiceMode ? '/menu' : path;
    path = shouldGoToCart ? '/cart' : path;

    // TODO: We should tweak the AuthRequired HOC to handle cases like this. Instead of redirecting users to auth routes, we now just want to use the auth modal.
    // https://rbictg.atlassian.net/browse/BKPE-14978
    if (shouldGoToCart && !isAuthenticated && !enableSignupAfterCart) {
      openSignInModal({
        navigateOnSuccessRoute: routes.cart,
      });
      return;
    }

    if (validatePreCurbsideOrder && validatePreCurbsideOrder()) {
      logEvent(eventName, EventTypes.Navigation, {
        'Hero URL': path,
        'Hero ID': itemId,
      });

      return navigate(path);
    }
    setCancelCurbsideOrderCallbackUrl(path);
  }, [
    validatePreCurbsideOrder,
    defaultLinkTo,
    eventName,
    isMatchingServiceMode,
    itemId,
    navigate,
    shouldGoToCart,
    isAuthenticated,
    enableSignupAfterCart,
    openSignInModal,
  ]);

  const continueToCartText = shouldGoToCart
    ? formatMessage({ id: 'continueToCart' }, { numberOfItemsInCart })
    : children;

  return (
    <ActionButtonStyled
      variant={primary ? ActionButtonVariants.PRIMARY : ActionButtonVariants.OUTLINE}
      eventAttributes={{ Name: shouldGoToCart ? continueToCartText : children }}
      leftIcon={<Icon variant={iconVariant} />}
      onPress={handleOnPress}
      marginTop={marginTop}
      backgroundColor={backgroundColor}
      _text={_text}
      accessibilityLabel={children}
    >
      {continueToCartText}
    </ActionButtonStyled>
  );
};

export const CTAButtons = ({
  numberOfItemsInCart,
  serviceMode,
  itemId,
  navigate,
}: ICTAButtonsProps) => {
  const { formatMessage } = useIntl();
  const { isPreConfirmCurbside } = useOrderContext();
  const { isAuthenticated } = useAuthContext();
  const { setShouldShowPreConfirmCurbsideModal } = useUIContext();

  const validatePreCurbsideOrder = () => {
    const shouldOpenModal = isPreConfirmCurbside() && isAuthenticated;

    setShouldShowPreConfirmCurbsideModal(shouldOpenModal);
    return !shouldOpenModal;
  };

  return (
    <AccessibleMainContent>
      <CTAButton
        primary
        defaultLinkTo={serviceMode ? routes.storeLocator : routes.menu}
        iconVariant="cart"
        isMatchingServiceMode={isPickup(serviceMode)}
        numberOfItemsInCart={numberOfItemsInCart}
        navigate={navigate}
        eventName={CustomEventNames.HERO_PRIMARY_CTA_CLICK}
        itemId={itemId}
        validatePreCurbsideOrder={validatePreCurbsideOrder}
      >
        {formatMessage({ id: 'orderPickup' })}
      </CTAButton>
      <CTAButton
        defaultLinkTo={routes.deliveryAddress}
        iconVariant="delivery"
        isMatchingServiceMode={isDelivery(serviceMode)}
        numberOfItemsInCart={numberOfItemsInCart}
        navigate={navigate}
        marginTop="$2"
        eventName={CustomEventNames.HERO_COMPLEMENTARY_CTA_CLICK}
        itemId={itemId}
        _text={{ color: primitive.bk.$bbqBrown }}
        validatePreCurbsideOrder={validatePreCurbsideOrder}
      >
        {formatMessage({ id: 'orderDelivery' })}
      </CTAButton>
    </AccessibleMainContent>
  );
};
